import { useState, useMemo, useEffect, useContext } from "react";
import { ModalBody, ModalHeaderWithActions, AdvancedCombobox } from "@evertrue/et-components";
import PropTypes from "prop-types";
import moment from "moment";
import SessionStore from "base/session/session-store";
import DatePickerDropdown from "components/calendar/date-picker-dropdown";
import FormField from "components/forms/form-field";
import TaskContact from "./task-contact";
import useTasks from "apps/tasks/hooks/use-tasks";
import CharacterCounter from "apps/interactions/components/character-counter";
import { TasksContext } from "apps/tasks/hooks/tasks-context";
import AddUser from "apps/search/components/add-user";

const TaskForm = ({ contact = null, handleClose, taskObj = {}, triggerReload = () => {} }) => {
  const currentUser = SessionStore.getUser();
  const { createTask, submitTaskDisabled, updateTask, taskActionTypes } = useTasks();
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [targetContact, setTargetContact] = useState(contact);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [actionType, setActionType] = useState({ label: "", value: "" });
  const [owner, setOwner] = useState({
    value: currentUser && currentUser.id,
    label: currentUser && currentUser.name + " (You)",
  });
  const [dueDate, setDueDate] = useState(moment.now());
  const payload = useMemo(buildPayload, [targetContact, title, description, actionType, owner, dueDate]);
  const { getAndSetTasks = () => {}, taskFilters } = useContext(TasksContext);
  const { contact_journey_id } = taskObj;

  useEffect(() => {
    if (Object.entries(taskObj).length) {
      // find full object in taskActionType based on value
      const selectedActionType = taskActionTypes.find((type) => type.value === taskObj.action_type);
      setTitle(taskObj.title);
      setDescription(taskObj.description);
      setActionType(selectedActionType);
      setDueDate(taskObj.due_date * 1000);
      setOwner({
        value: taskObj.owner_user_id,
        label:
          currentUser && taskObj.owner_user_id === currentUser.id
            ? taskObj.owner_info.name + " (You)"
            : taskObj.owner_info.name,
      });
      if (taskObj.target_info) {
        setTargetContact({
          id: taskObj.target_contact_id,
          name: taskObj.target_info.contact_attributes.name_full,
          avatar: taskObj.target_info.profile_image,
        });
      }
    }
  }, [currentUser, taskObj, taskActionTypes]);

  // Build Payload
  function buildPayload() {
    return {
      title,
      description,
      owner_user_id: owner ? owner.value : null,
      action_type: actionType ? actionType.value : null,
      due_date: Math.floor(dueDate / 1000),
      target_contact_id: targetContact ? targetContact.id : null,
      completed_at: null,
      deleted_at: null,
    };
  }

  // Validate Payload
  useEffect(() => {
    if (submitTaskDisabled(payload, !contact_journey_id)) setSubmitDisabled(true);
    else setSubmitDisabled(false);
  }, [payload, submitTaskDisabled, contact_journey_id]);

  // Submit Handler
  const handleSubmit = async () => {
    if (payload.due_date !== 0) {
      let formattedTime = moment
        .unix(payload.due_date)
        .hour(12 + moment.unix(payload.due_date).utcOffset() / 60)
        .minute(0)
        .second(0);
      formattedTime = Math.floor(formattedTime.valueOf() / 1000);
      payload.due_date = formattedTime;
    } else {
      payload.due_date = null;
    }

    setSubmitDisabled(true);
    handleClose();

    if (Object.entries(taskObj).length) await updateTask(payload, taskObj);
    else await createTask(payload);

    triggerReload();
    setSubmitDisabled(false);
    getAndSetTasks(taskFilters);
  };

  return (
    <>
      <ModalHeaderWithActions
        title={`${Object.entries(taskObj).length ? "Edit" : "Add"} Task`}
        closeModal={handleClose}
        onSubmit={handleSubmit}
        disableSubmit={submitDisabled}
      />
      <ModalBody scrollable={false}>
        <TaskContact targetContact={targetContact} setTargetContact={setTargetContact} onContactPage={!!contact} />
        <FormField required={true} label="Task Name" inlineLabel={false}>
          <input
            type="text"
            value={title}
            placeholder={`Enter the Task Name...`}
            onChange={(e) => setTitle(e.currentTarget.value)}
            disabled={!!contact_journey_id}
          />
          <CharacterCounter limit={120} count={title.length} />
        </FormField>
        <FormField label="Description" inlineLabel={false}>
          <textarea
            value={description}
            placeholder="Add Details..."
            onChange={(e) => setDescription(e.currentTarget.value)}
            disabled={!!contact_journey_id}
          />
          <CharacterCounter limit={250} count={description.length} />
        </FormField>
        <div className="task-options">
          <FormField required={!contact_journey_id} label="Action Type" inlineLabel={false}>
            <AdvancedCombobox
              options={taskActionTypes}
              onChange={(task) => setActionType(task)}
              value={actionType}
              placeholder="Select Type..."
              disabled={!!contact_journey_id}
            />
          </FormField>
          <FormField label="Assignee" inlineLabel={false}>
            <AddUser handleOnChange={(user) => setOwner(user)} value={owner} disabled={!!contact_journey_id} />
          </FormField>
        </div>
        <FormField label="Due Date" inlineLabel={false}>
          <DatePickerDropdown
            date={dueDate}
            onDayClick={(start) => setDueDate(start)}
            triggerClassName={"task-calendar"}
            chevronClassName={"date-picker-chevron"}
            allowClear
          />
        </FormField>
      </ModalBody>
    </>
  );
};

TaskForm.propTypes = {
  contact: PropTypes.object,
  handleClose: PropTypes.func,
  taskObj: PropTypes.object,
  triggerReload: PropTypes.func,
};

export default TaskForm;
